export interface Camera {
  x: number
  y: number
  zoom: number
  /** Aspect-compensated zoom: on wider-than-reference screens, shows more world in both axes. */
  readonly effectiveZoom: number
  viewportWidth: number
  viewportHeight: number
  setViewport(w: number, h: number): void
  screenToWorld(sx: number, sy: number): { x: number; y: number }
  worldToScreen(wx: number, wy: number): { x: number; y: number }
  /** Clip-space matrix for shader: world units → NDC (column-major 4x4). */
  viewProjection(): Float32Array
  shake(intensity: number, duration: number): void
  /** Multiply zoom by `mult` now, easing back to neutral over `durationSec`.
   *  In the microscope fiction this is the observer leaning in (mult > 1)
   *  or sitting back (mult < 1). */
  zoomPulse(mult: number, durationSec: number): void
  tickShake(dt: number): void
  resetShake(): void
}

// Widescreen fairness: as aspect > 1 (wide), effective zoom shrinks so vertical
// extent grows too. At 16:9 aspect ~1.78, effZoom = zoom / sqrt(1.78) ≈ 0.75 * zoom,
// i.e. ~33% more world visible in both axes compared to square.
const REFERENCE_ASPECT = 1.0
function computeEffectiveZoom(zoom: number, aspect: number): number {
  return zoom / Math.max(1, Math.sqrt(aspect / REFERENCE_ASPECT))
}

export function createCamera(): Camera {
  const state = {
    x: 0,
    y: 0,
    zoom: 1,
    viewportWidth: 1,
    viewportHeight: 1,
    shakeAmt: 0,
    shakeIntensity: 0,
    zoomPulseMult: 1,
    zoomPulseRemaining: 0,
    zoomPulseDuration: 1,
  }

  // Base zoom with any active pulse folded in (smooth release back to 1).
  function pulsedZoom(): number {
    if (state.zoomPulseRemaining <= 0) return state.zoom
    const k = state.zoomPulseRemaining / state.zoomPulseDuration // 1 → 0
    const ease = k * k * (3 - 2 * k)
    return state.zoom * (1 + (state.zoomPulseMult - 1) * ease)
  }

  return {
    get x() {
      return state.x
    },
    set x(v) {
      state.x = v
    },
    get y() {
      return state.y
    },
    set y(v) {
      state.y = v
    },
    get zoom() {
      return state.zoom
    },
    set zoom(v) {
      state.zoom = Math.max(0.05, Math.min(v, 50))
    },
    get effectiveZoom() {
      const aspect = state.viewportWidth / state.viewportHeight
      return computeEffectiveZoom(pulsedZoom(), aspect)
    },
    get viewportWidth() {
      return state.viewportWidth
    },
    get viewportHeight() {
      return state.viewportHeight
    },
    setViewport(w, h) {
      state.viewportWidth = Math.max(1, w)
      state.viewportHeight = Math.max(1, h)
    },
    screenToWorld(sx, sy) {
      const nx = (sx / state.viewportWidth) * 2 - 1
      const ny = 1 - (sy / state.viewportHeight) * 2
      const aspect = state.viewportWidth / state.viewportHeight
      const z = computeEffectiveZoom(pulsedZoom(), aspect)
      return {
        x: state.x + (nx * aspect) / z,
        y: state.y + ny / z,
      }
    },
    worldToScreen(wx, wy) {
      const aspect = state.viewportWidth / state.viewportHeight
      const z = computeEffectiveZoom(pulsedZoom(), aspect)
      const nx = ((wx - state.x) * z) / aspect
      const ny = (wy - state.y) * z
      return {
        x: ((nx + 1) / 2) * state.viewportWidth,
        y: ((1 - ny) / 2) * state.viewportHeight,
      }
    },
    viewProjection() {
      const aspect = state.viewportWidth / state.viewportHeight
      const z = computeEffectiveZoom(pulsedZoom(), aspect)
      const sx = z / aspect
      const sy = z
      const ox = state.shakeAmt > 0 ? (Math.random() * 2 - 1) * state.shakeIntensity : 0
      const oy = state.shakeAmt > 0 ? (Math.random() * 2 - 1) * state.shakeIntensity : 0
      const tx = -(state.x + ox) * sx
      const ty = -(state.y + oy) * sy
      // column-major 4x4
      // prettier-ignore
      return new Float32Array([
        sx,  0, 0, 0,
         0, sy, 0, 0,
         0,  0, 1, 0,
        tx, ty, 0, 1,
      ])
    },
    shake(intensity: number, duration: number) {
      state.shakeIntensity = intensity
      state.shakeAmt = duration
    },
    zoomPulse(mult: number, durationSec: number) {
      state.zoomPulseMult = mult
      state.zoomPulseDuration = Math.max(0.01, durationSec)
      state.zoomPulseRemaining = state.zoomPulseDuration
    },
    tickShake(dt: number) {
      if (state.shakeAmt > 0) {
        state.shakeAmt = Math.max(0, state.shakeAmt - dt)
      }
      if (state.zoomPulseRemaining > 0) {
        state.zoomPulseRemaining = Math.max(0, state.zoomPulseRemaining - dt)
      }
    },
    resetShake() {
      state.shakeAmt = 0
      state.shakeIntensity = 0
      state.zoomPulseRemaining = 0
    },
  }
}
